import { useEffect, useState, type RefObject } from "react"

import type { DocHeading } from "@javascript/components/developer-docs/DeveloperDocsToc"

function slugify(text: string) {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function useDocHeadings(containerRef: RefObject<HTMLElement | null>) {
  const [headings, setHeadings] = useState<DocHeading[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const elements = Array.from(container.querySelectorAll<HTMLHeadingElement>("h2"))
    elements.forEach((element) => {
      if (!element.id) element.id = slugify(element.textContent ?? "")
    })
    setHeadings(elements.map((element) => ({ id: element.id, text: element.textContent ?? "" })))

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting)
        if (visibleEntry) setActiveId(visibleEntry.target.id)
      },
      { rootMargin: "0px 0px -70% 0px" }
    )
    elements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [containerRef])

  return { headings, activeId }
}
